import {useState} from 'react';
import PropTypes from 'prop-types';
import isNumber from 'lodash/isNumber';
import moment from 'moment';
// import {useNavigate} from 'react-router-dom';

// import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from "@mui/material/Typography";
import UploadIcon from '@mui/icons-material/Upload';

import CustomNumberField from '../common/CustomNumberField';
import CustomSelect from '../common/CustomSelect';
import NoteEditor from '../common/NoteEditor';
import FileUploadInput from '../common/FileUploadInput';
import ClaimFileDownloadButton from './ClaimFileDownloadButton';
import ClaimStatusIcon from './ClaimStatusIcon';
import ClaimDateEditor from './ClaimDateEditor';

import {formatCaseBillingType} from '../../helpers/cases';
import {formatPatientName} from '../../helpers/patients';
import {formatProviderName} from '../../helpers/providers';
import {formatDate} from '../../helpers/dates';
import {getClaimStatuses} from '../../helpers/selectOptions';
import {useUpdateClaimMutation} from '../../redux/services/claimApi';
import {useCreateFileMutation} from '../../redux/services/fileApi';

const getColumns = ({
  onFieldChange, 
  updating, 
  hasSuccess, 
  hasUpdateError,
  onOpenFileUploader,
}) => [
  {
    key: 'status',
    // renderCellContent: c => <EditCaseStatus case={c} />,
    renderCellContent: c => (
      <Stack direction='row' spacing={0.25} alignItems='center'>
        {
          <ClaimStatusIcon claim={c} />
        }
        <CustomSelect
          defaultValue={c.status}
          options={getClaimStatuses()}
          style={{minWidth: '150px'}}
          onChange={onFieldChange('status')}
        />
        {
          updating === 'status' && hasUpdateError &&
          <DoNotDisturbIcon color='error' />
        }
        {
          updating === 'status' && hasSuccess  &&
          <CheckIcon color='success' />
        }
      </Stack>
    ),
  },
  {
    key: 'patient',
    renderCellContent: c => formatPatientName(c.patient),
  },
  {
    key: 'date',
    align: 'center',
    renderCellContent: c => (
      <Typography variant='small500Inter'>
          {
          c?.session?.date 
          ? formatDate(c.session.date, 'M/D/YYYY')
          : '-'
        }
      </Typography>
    ),
  },
  {
    key: 'provider',
    renderCellContent: c => formatProviderName(c.provider),
  },
  {
    key: 'billing_category',
    renderCellContent: c => formatCaseBillingType(c?.case, {includePlanCategory: true}),
  },
  {
    key: 'amount_billed',
    align: 'center',
    renderCellContent: c => isNumber(c.amount_billed) ? '$' + c.amount_billed : '',
  },
  {
    key: 'total',
    align: 'center',
    renderCellContent: c => isNumber(c?.session?.total) ? '$' + c.session.total : '',
  },
  {
    key: 'amount_paid',
    align: 'center',
    renderCellContent: c => (
      <Stack direction='row' spacing={1}>
        {
          updating === 'amount_paid' && hasUpdateError &&
          <DoNotDisturbIcon color='error' />
        }
        {
          updating === 'amount_paid' && hasSuccess  &&
          <CheckIcon color='success' />
        }
        <CustomNumberField
          defaultValue={isNumber(c.amount_paid) ? c.amount_paid : ''}
          style={{width: '90px'}}
          onChange={onFieldChange('amount_paid')}
        />
      </Stack>
    ),
  },
  {
    key: 'time_paid',
    align: 'center',
    renderCellContent: c => (
      <ClaimDateEditor 
        date={c?.time_paid}
        onChange={onFieldChange('time_paid')} 
      />
    ),
  },
  {
    key: 'provider_payout',
    align: 'center',
    renderCellContent: c => isNumber(c?.session?.provider_claim_payout) ? '$' + c.session.provider_claim_payout : '',
  },
  {
    key: 'payer',
    renderCellContent: c => c?.case?.payer_plan?.payer ? c?.case?.payer_plan?.payer.name : '-',
  },
  {
    key: 'facility',
    renderCellContent: c => c?.provider?.facility?.name,
  },
  {
    key: 'eob',
    renderCellContent: c => (
      c?.eob_file 
      ? <ClaimFileDownloadButton claim={c} />
      : (
        <Stack alignItems='center'>
          <IconButton 
            variant='contained'
            color='primary'
            disabled={c?.eob_file?.url}
            onClick={onOpenFileUploader}
            allowTitle={true}
          >
            <UploadIcon />
          </IconButton>
        </Stack>
      )
    ),
  },
  {
    key: 'note',
    renderCellContent: c => (
      <NoteEditor 
        note={c.note} 
        onSubmit={onFieldChange('note')}
      />
    ),
  },
];

const handleFieldChange = ({id, put, setUpdating}) => field => async e => {
  setUpdating(field);
  const body = {[field]: e.target.value};
  const request = await put({id, body});
};

const handleOpenFileUploader = ({setIsOpen}) => e => {
  e.stopPropagation();
  setIsOpen(true);
};

const FileUploadDialog = props => {
  const {isOpen, onClose, claimId, patientId} = props;
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth={true}
      maxWidth='sm'
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContent>
        <Typography variant='h2' padding={1}>
          Upload Claim EOB File
        </Typography>
        <Divider/>
        <Stack spacing={1} padding={1}>
          <FileUploadInput
            claimId={claimId}
            patientId={patientId}
            fileType='patient_file'
            patientFileCategory='CLAIM_EOB'
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ClaimTableRow = props => {
  // const navigate = useNavigate();
  const [updating, setUpdating] = useState(null);
  const [fileUploaderIsOpen, setIsFileUploaderIsOpen] = useState(false);
  const {data: c} = props;
  const [put, request] = useUpdateClaimMutation();
  const onFieldChange = handleFieldChange({
    id: c.id,
    put,
    setUpdating,
  });
  const onOpenFileUploader = handleOpenFileUploader({
    setIsOpen: setIsFileUploaderIsOpen,
  });
  const columns = getColumns({
    onFieldChange, 
    updating, 
    hasUpdateError: !!request?.error,
    hasSuccess: request?.isSuccess,
    onOpenFileUploader,
  })
  .filter(col => props?.columns ? props?.columns.includes(col.key) : true);
  return (
    <TableRow
      key={c.id}
    >
      <FileUploadDialog
        claimId={c.id}
        patientId={c.patient.id}
        isOpen={fileUploaderIsOpen}
        onClose={() => setIsFileUploaderIsOpen(false)}
      />
      {columns.map((col, i) => (
        <TableCell 
          key={i} 
          align={col.align || 'left'} 
        >
          {col.renderCellContent(c, {request})}
        </TableCell>
      ))}
    </TableRow>
  );
};

ClaimTableRow.propTypes = {
  data: PropTypes.object,
  // buttons: PropTypes.arrayOf(PropTypes.element),
  // isPatient: PropTypes.bool,
  // afterSave: PropTypes.func,
};

export default ClaimTableRow;
